/**
 * Frontline admin — queries + mutations over the existing audited
 * admin handler (/api/tenant/v1/frontline/*; the admin api-client
 * rewrites /api/v1/tenants/{id}/… → /api/tenant/v1/… on a tenant
 * host). Reads gate on auth_methods.read; mutations on
 * auth_methods.manage + a fresh step-up — withStepUp drives the
 * global ceremony dialog.
 *
 * The one-time pairing secret lives ONLY in the mutation's resolved
 * value (revealed once in a dialog). It is NEVER setQueryData'd /
 * cached — onSuccess invalidates the station list only (CLAUDE.md
 * secrets rule).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { withStepUp } from "@/lib/with-step-up";
import { queryKeys } from "@/lib/query-keys";
import {
  frontlineConfigSchema,
  frontlineGroupsResponseSchema,
  stationsResponseSchema,
  pairStationResponseSchema,
  shiftLeadResponseSchema,
  type FrontlineConfig,
  type FrontlineGroup,
  type Station,
  type PairStationResponse,
} from "./schemas";
import {
  badgesResponseSchema,
  issueBadgeResponseSchema,
  issueTempQRResponseSchema,
  resetPinResponseSchema,
  stationDetailSchema,
  tempQRsResponseSchema,
} from "./api.generated";
import type {
  FrontlineBadge,
  FrontlineBadgeIssued,
  FrontlineBadgeRevokeReason,
  FrontlinePinReset,
  FrontlinePinResetReason,
  FrontlineStationDetail,
  FrontlineTempQR,
  FrontlineTempQRIssued,
  FrontlineTempQRRevokeReason,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/frontline`;
}
const key = (tenantID: string) => queryKeys.frontline.all(tenantID);

export async function fetchFrontlineConfig(tenantID: string): Promise<FrontlineConfig> {
  return frontlineConfigSchema.parse(await apiFetch<unknown>(`${base(tenantID)}/config`));
}

export async function fetchStations(tenantID: string): Promise<Station[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/stations`);
  return stationsResponseSchema.parse(raw).stations ?? [];
}

export function useFrontlineConfigQuery(tenantID: string) {
  return useQuery<FrontlineConfig, ApiError>({
    queryKey: [...key(tenantID), "config"],
    queryFn: () => fetchFrontlineConfig(tenantID),
    enabled: tenantID.length > 0,
  });
}

export function useStationsQuery(tenantID: string) {
  return useQuery<Station[], ApiError>({
    queryKey: [...key(tenantID), "stations"],
    queryFn: () => fetchStations(tenantID),
    enabled: tenantID.length > 0,
    refetchInterval: 30_000,
  });
}

export interface PairStationInput {
  product_area: "badge_sign_in" | "kiosk_pass"; station_name: string;
  location_label?: string;
}

export function usePairStationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<PairStationResponse, ApiError, PairStationInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/stations`, {
          method: "POST",
          body: {
            product_area: input.product_area,
            station_name: input.station_name,
            location_label: input.location_label || undefined,
          },
        }).then((r) => pairStationResponseSchema.parse(r)),
      ),
    // The pairing secret rides ONLY in this resolved value. Never
    // cache it — just refresh the list.
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...key(tenantID), "stations"] });
      void qc.invalidateQueries({ queryKey: ["productAreas", tenantID] });
    },
  });
}

/**
 * Reissue one station's pairing code.
 *
 * NOT a resend of a code that was never spent. The station browser presents this secret on every
 * request, so this rotates a live credential: whatever browser held the previous value stops
 * authenticating the moment this resolves. That is the point when a code is lost — setup registers
 * these stations, so the operator never saw the original and the console cannot show it back — and
 * it is the cost when the station is already paired. The caller confirms before calling.
 *
 * The new secret rides ONLY in the resolved value, exactly as pairing does. Never cache it.
 */
export function useReissueStationPairingMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<PairStationResponse, ApiError, { stationId: string }>({
    mutationFn: ({ stationId }) =>
      withStepUp(() =>
        apiFetch<unknown>(
          `${base(tenantID)}/stations/${encodeURIComponent(stationId)}/reissue-pairing`,
          { method: "POST" },
        ).then((r) => pairStationResponseSchema.parse(r)),
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...key(tenantID), "stations"] });
    },
  });
}

export function useUnpairStationMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { stationId: string; reason: string }>({
    mutationFn: ({ stationId, reason }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `${base(tenantID)}/stations/${encodeURIComponent(stationId)}/unpair`,
          { method: "POST", body: { reason } },
        );
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...key(tenantID), "stations"] });
      void qc.invalidateQueries({ queryKey: ["productAreas", tenantID] });
    },
  });
}

export function useClearQuarantineMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { stationId: string }>({
    mutationFn: ({ stationId }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `${base(tenantID)}/stations/${encodeURIComponent(stationId)}/clear-quarantine`,
          { method: "POST", body: { reason: "manager_attested" } },
        );
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...key(tenantID), "stations"] });
      void qc.invalidateQueries({ queryKey: ["productAreas", tenantID] });
    },
  });
}

export function useShiftLeadMutation(tenantID: string) {
  return useMutation<boolean, ApiError, { userId: string; grant: boolean }>({
    mutationFn: ({ userId, grant }) =>
      withStepUp(async () => {
        const path = `${base(tenantID)}/users/${encodeURIComponent(userId)}/shift-lead${
          grant ? "" : "/revoke"
        }`;
        const r = await apiFetch<unknown>(path, { method: "POST" });
        return shiftLeadResponseSchema.parse(r).is_shift_lead;
      }),
  });
}

export async function fetchFrontlineGroups(tenantID: string): Promise<FrontlineGroup[]> {
  const raw = await apiFetch<unknown>(`${base(tenantID)}/groups`);
  return frontlineGroupsResponseSchema.parse(raw).groups ?? [];
}

export function useFrontlineGroupsQuery(tenantID: string) {
  return useQuery<FrontlineGroup[], ApiError>({
    queryKey: [...key(tenantID), "groups"],
    queryFn: () => fetchFrontlineGroups(tenantID),
    enabled: tenantID.length > 0,
  });
}

export function useSetFrontlineGroupMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { groupId: string; frontline: boolean }>({
    mutationFn: ({ groupId, frontline }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `${base(tenantID)}/groups/${encodeURIComponent(groupId)}`,
          { method: "POST", body: { frontline } },
        );
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: [...key(tenantID), "groups"] }),
  });
}

// Enable / disable the Badge sign-in offering from the console. The
// write is the tenant-authentication PATCH — NOT a /frontline/*
// endpoint — because frontline_enabled is tenant config: the tenant's
// own switch behind a fresh step-up, allowed on only while the
// Frontline product area is on (the area is the master switch,
// ; the server refuses otherwise with a 422 on the
// "frontline_enabled" field, as it does for the workforce-only and
// no-dead-end guards; the ApiError message carries the actionable
// reason). On success the frontline config query is invalidated so
// the posture + the page banner re-render.


// Kiosk Pass (roaming sign-in hand-off) offering switch. Same
// guarded path as frontline_enabled — PATCH /authentication with
// {frontline_kiosk_pass_enabled}, step-up + area-on + audited
// (tenant.authentication.updated change diff). It is an
// OFFERING switch (the 6-digit voucher's safety is in-code and not
// tenant-tunable), so a console toggle is appropriate; break_glass
// deliberately stays CLI-only as an escalation bit.
// No no-dead-end 422 server-side (enabling while frontline routing
// is off is inert, not stranding); the UI guides that dependency.


// ─── Kiosk Pass per-method policy ─────────────────────────────────
//
// The boolean master toggle (frontline_kiosk_pass_enabled) is
// flipped by useSetFrontlineKioskPassMutation above. Below: the
// finer-grained per-method list and its PATCH.
//
// The "methods" the kiosk SPA renders as tiles (phone passkey via
// QR, FIDO2 NFC card, future HID prox / PIV / etc.) are discovered
// at runtime from the backend's method-dispatcher registry. The
// admin console reads the descriptor list to render a toggle per
// available method, and PATCHes the policy as a single array.
//
// Architecture: docs/architecture.md.

export interface KioskPassMethodDescriptor {
  readonly id: string;
  readonly assurance: string;
  readonly display_name: string;
  readonly short_description: string;
  readonly requires_kiosk_agent: boolean;
}

export interface KioskPassMethodEntry {
  readonly id: string;
  readonly enabled: boolean;
  // Per-method config is method-specific; opaque to the toggle UI.
  // T0 methods carry empty objects; T1+ methods may surface custom
  // controls here in dedicated per-method form fragments.
  readonly config: Record<string, unknown>;
}

export async function fetchKioskPassMethodDescriptors(
  tenantID: string,
): Promise<KioskPassMethodDescriptor[]> {
  const raw = await apiFetch<{ methods?: KioskPassMethodDescriptor[] }>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/kiosk-pass/methods`,
  );
  return raw.methods ?? [];
}

export function useKioskPassMethodDescriptorsQuery(tenantID: string) {
  return useQuery<KioskPassMethodDescriptor[], ApiError>({
    queryKey: [...key(tenantID), "kiosk-pass-methods"],
    queryFn: () => fetchKioskPassMethodDescriptors(tenantID),
    enabled: tenantID.length > 0,
    // Method ids change only on deploy. A 5-minute stale window
    // keeps the toggle UI responsive without re-fetching on every
    // page mount.
    staleTime: 5 * 60_000,
  });
}

// useSetFrontlineKioskPassMethodsMutation — replaces the tenant's
// per-method policy in one PATCH. Empty list means "T0 default:
// every registered method enabled when the master is ON"; a
// non-empty list is the explicit per-method curation.
//
// Rides the same audited PATCH /authentication path as the other
// frontline toggles; the diff carries frontline_kiosk_pass_methods.
// ─── Kiosk Pass readiness check ──────────────────────────────────
//
// GET /api/v1/tenants/{id}/kiosk-pass/health runs every deploy-
// side check the admin console wants to surface — registry,
// tenant policy, deploy dependencies, kiosk fleet — and returns
// a structured checklist. The admin clicks "Run readiness check"
// in the Kiosk Pass card; the SPA renders the rows.
//
// Single round-trip; the report is cheap to recompute, so the
// SPA refetches on demand rather than caching.

export interface KioskPassHealthCheck {
  readonly id: string;
  readonly label: string;
  readonly ok: boolean;
  readonly critical: boolean;
  readonly detail?: string;
  readonly remediation?: string;
}

export interface KioskPassHealthReport {
  readonly overall: "ok" | "degraded" | "blocked";
  readonly checks: KioskPassHealthCheck[];
}

export async function fetchKioskPassHealth(
  tenantID: string,
): Promise<KioskPassHealthReport> {
  return apiFetch<KioskPassHealthReport>(
    `/api/v1/tenants/${encodeURIComponent(tenantID)}/kiosk-pass/health`,
  );
}

export function useKioskPassHealthQuery(
  tenantID: string,
  options: { enabled?: boolean } = {},
) {
  return useQuery<KioskPassHealthReport, ApiError>({
    queryKey: [...key(tenantID), "kiosk-pass-health"],
    queryFn: () => fetchKioskPassHealth(tenantID),
    enabled: tenantID.length > 0 && (options.enabled ?? false),
    // The admin runs this on demand; no automatic polling.
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
}

export function useSetFrontlineKioskPassMethodsMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { methods: readonly KioskPassMethodEntry[] }>({
    mutationFn: ({ methods }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(tenantID)}/authentication`,
          {
            method: "PATCH",
            body: { frontline_kiosk_pass_methods: methods },
          },
        );
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["productAreas", tenantID] });
      void qc.invalidateQueries({ queryKey: [...key(tenantID), "config"] });
      // The methods list lives on the tenant row, not the frontline
      // config endpoint, so we don't invalidate the frontline config.
      // Invalidate the tenant query and the kiosk-pass-methods read.
      void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantID] });
      void qc.invalidateQueries({
        queryKey: [...key(tenantID), "kiosk-pass-methods"],
      });
    },
  });
}

// Single-select of the Badge sign-in method. "Allowed badge types"
// is a single-select control — a tenant runs EITHER Badge + PIN OR
// NFC tap-cards, never both — so switching methods must set BOTH
// flags in ONE PATCH: the chosen method on, the other off, atomically.
// Sending two separate toggles would pass through a transient
// both-off state, which the server invariant (FrontlineEnabled ⇒
// pin OR nfc) rejects with a 422. Same guarded path as the offering
// switch — PATCH /authentication, step-up, audited via the
// tenant.authentication.updated change diff. The runtime ceremonies
// (Tier 1 verify-pin / Tier 2 tap gate at the Terminal) read the
// SAME columns this writes, so the kiosk honours the choice
// immediately on its next /config fetch.
/**
 * The station's vocabulary. "Shift lead" is manufacturing and retail
 * wording that reads as boilerplate to clinical staff — a ward says
 * CHARGE NURSE — and the identifier is variously a payroll number, a
 * worker ID or an assignment number. Both are presentation only: no
 * authorization, matching or ceremony decision reads them, and the
 * claim stays opaque at the station whatever it is called.
 *
 * An empty string CLEARS the override back to the product default
 * rather than storing a blank word. Same guarded path as every other
 * frontline setting: PATCH /authentication, step-up, audited through
 * the tenant.authentication.updated change diff.
 */
export function useSetFrontlineVocabularyMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { authority: string; claim: string }>({
    mutationFn: ({ authority, claim }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(tenantID)}/authentication`,
          {
            method: "PATCH",
            body: {
              frontline_authority_label: authority,
              frontline_claim_label: claim,
            },
          },
        );
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...key(tenantID), "config"] });
    },
  });
}

export function useSelectFrontlineBadgeMethodMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { method: "pin" | "nfc" }>({
    mutationFn: ({ method }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `/api/v1/tenants/${encodeURIComponent(tenantID)}/authentication`,
          {
            method: "PATCH",
            body: {
              frontline_pin_method_enabled: method === "pin",
              frontline_nfc_method_enabled: method === "nfc",
            },
          },
        );
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: [...key(tenantID), "config"] });
      void qc.invalidateQueries({ queryKey: ["productAreas", tenantID] });
    },
  });
}

// ─── One station ──────────────────────────────────────────────────
//
// The station list is the operator's overview; this read is the record behind one row, fetched
// fresh when the operator opens it rather than trusted from the list snapshot.

export async function fetchStation(tenantID: string, stationId: string): Promise<FrontlineStationDetail> {
  return stationDetailSchema.parse(
    await apiFetch<unknown>(`${base(tenantID)}/stations/${encodeURIComponent(stationId)}`),
  );
}

export function useStationQuery(tenantID: string, stationId: string | null) {
  return useQuery<FrontlineStationDetail, ApiError>({
    queryKey: [...key(tenantID), "station", stationId ?? ""],
    queryFn: () => fetchStation(tenantID, stationId ?? ""),
    enabled: tenantID.length > 0 && Boolean(stationId),
  });
}

// ─── One worker's frontline credentials ───────────────────────────
//
// Badges, recovery codes and the PIN a worker uses at a station. Reads need auth_methods.read;
// every write needs auth_methods.manage with a fresh step-up, which withStepUp drives.
//
// Three of these writes mint a secret: a tier 1 badge nonce, a recovery code and a temporary PIN.
// Each secret rides ONLY in the mutation's resolved value, for the caller to reveal once. Nothing
// here calls setQueryData; onSuccess only invalidates the worker's lists (CLAUDE.md secrets rule).

function workerBase(tenantID: string, userId: string): string {
  return `${base(tenantID)}/users/${encodeURIComponent(userId)}`;
}

export const workerKeys = {
  badges: (tenantID: string, userId: string) => [...key(tenantID), "users", userId, "badges"] as const,
  tempQRs: (tenantID: string, userId: string) => [...key(tenantID), "users", userId, "temp-qrs"] as const,
};

export async function fetchWorkerBadges(tenantID: string, userId: string): Promise<FrontlineBadge[]> {
  const raw = await apiFetch<unknown>(`${workerBase(tenantID, userId)}/badges`);
  return badgesResponseSchema.parse(raw).badges;
}

export async function fetchWorkerTempQRs(tenantID: string, userId: string): Promise<FrontlineTempQR[]> {
  const raw = await apiFetch<unknown>(`${workerBase(tenantID, userId)}/temp-qrs`);
  return tempQRsResponseSchema.parse(raw).temp_qrs;
}

export function useWorkerBadgesQuery(tenantID: string, userId: string) {
  return useQuery<FrontlineBadge[], ApiError>({
    queryKey: workerKeys.badges(tenantID, userId),
    queryFn: () => fetchWorkerBadges(tenantID, userId),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

export function useWorkerTempQRsQuery(tenantID: string, userId: string) {
  return useQuery<FrontlineTempQR[], ApiError>({
    queryKey: workerKeys.tempQRs(tenantID, userId),
    queryFn: () => fetchWorkerTempQRs(tenantID, userId),
    enabled: tenantID.length > 0 && userId.length > 0,
  });
}

/** Issue a tier 1 badge. The printable nonce is in the resolved value only. */
export function useIssueBadgeMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<FrontlineBadgeIssued, ApiError, { expiresAt: string | null }>({
    mutationFn: ({ expiresAt }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${workerBase(tenantID, userId)}/badges`, {
          method: "POST",
          body: { tier: "tier1", expires_at: expiresAt },
        }).then((r) => issueBadgeResponseSchema.parse(r)),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workerKeys.badges(tenantID, userId) }),
  });
}

export function useRevokeBadgeMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { badgeId: string; reason: FrontlineBadgeRevokeReason }>({
    mutationFn: ({ badgeId, reason }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(
          `${workerBase(tenantID, userId)}/badges/${encodeURIComponent(badgeId)}/revoke`,
          { method: "POST", body: { reason } },
        );
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workerKeys.badges(tenantID, userId) }),
  });
}

/**
 * Issue a recovery code. Issuing ends any earlier live code for the worker, so the list is
 * refreshed; the digits are in the resolved value only.
 */
export function useIssueTempQRMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<FrontlineTempQRIssued, ApiError, { expiresInMinutes: number | null }>({
    mutationFn: ({ expiresInMinutes }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${workerBase(tenantID, userId)}/temp-qr`, {
          method: "POST",
          body: expiresInMinutes === null ? {} : { expires_in_minutes: expiresInMinutes },
        }).then((r) => issueTempQRResponseSchema.parse(r)),
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workerKeys.tempQRs(tenantID, userId) }),
  });
}

/** End the worker's live recovery code. A worker holds at most one, so this is by worker. */
export function useRevokeTempQRMutation(tenantID: string, userId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, { reason: FrontlineTempQRRevokeReason }>({
    mutationFn: ({ reason }) =>
      withStepUp(async () => {
        await apiFetch<unknown>(`${workerBase(tenantID, userId)}/temp-qr/revoke`, {
          method: "POST",
          body: { reason },
        });
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: workerKeys.tempQRs(tenantID, userId) }),
  });
}

/** Reset the worker's PIN. The temporary PIN is in the resolved value only; there is no list. */
export function useResetPinMutation(tenantID: string, userId: string) {
  return useMutation<FrontlinePinReset, ApiError, { reason: FrontlinePinResetReason }>({
    mutationFn: ({ reason }) =>
      withStepUp(() =>
        apiFetch<unknown>(`${workerBase(tenantID, userId)}/pin/reset`, {
          method: "POST",
          body: { reset_reason: reason },
        }).then((r) => resetPinResponseSchema.parse(r)),
      ),
  });
}
